import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
// import * as React from "react"; // Custom Code
import { css } from "styled-components";

/* Custom Code */
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import scrollToId from "@store/Framework/Common/Utilities/scrollToId";
import stripHtml from "@store/Framework/Common/Utilities/stripHtml";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { lineEllipsisStyles } from "@store/Themes/common/mixins/typography";
import React from "react";

const enum fields {
    shortDescription = "shortDescription",
    anchor = "anchor",
}
/* Custom Code */

type Props = WidgetProps & HasProduct;

export interface ProductDetailsDescriptionStyles {
    wrapper?: InjectableCss;
    // Custom Code
    noResult?: TypographyPresentationProps;
    shortDescriptionWrapper?: InjectableCss;
    shortDescription?: InjectableCss;
    productReadMoreLink?: LinkPresentationProps;
}

const maxLine = 3;

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    noResult: {
        forwardAs: "p",
    },
    shortDescriptionWrapper: {
        css: css`
            ${({ theme }) => {
                const spacing = theme.design.product.detail.contentSpacing;
                return spacingStyles(spacing, spacing);
            }}

            ${breakpoint("sm", "down")} {
                display: none;
            }
        `,
    },
    shortDescription: {
        css: css`
            ${lineEllipsisStyles(maxLine)}
            ${({ theme }) => {
                const fontSize = theme.design.typography.size.base;
                const lineHeight = theme.design.typography.lineHeight.base;

                return css`
                    font-size: ${fontSize};
                    line-height: ${lineHeight};
                    max-height: calc((${lineHeight} * ${fontSize}) * ${maxLine});
                `;
            }}
        `,
    },
    productReadMoreLink: {
        css: css`
            ${linkActionStyles()}
            ${spacingStyles("0.25em", null)}
        `,
    },
};

const styles = descriptionStyles;

const ProductDetailsDescription: React.FC<Props> = ({ product, fields: { shortDescription, anchor } }) => {
    /* Custom Code */
    if (shortDescription) {
        if (!product.content?.htmlContent) {
            return null;
        }

        return (
            <StyledWrapper {...styles.shortDescriptionWrapper}>
                <StyledWrapper {...styles.shortDescription} data-test-selector="productDetails_htmlContent">
                    {parse(stripHtml(product.content.htmlContent), parserOptions)}
                </StyledWrapper>
                {anchor ? (
                    <Link {...styles.productReadMoreLink} onClick={() => scrollToId(anchor)}>
                        {translate("Read full description")}
                    </Link>
                ) : null}
            </StyledWrapper>
        );
    }

    if (!product.content?.htmlContent) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_htmlContent">
                <Typography {...styles.noResult}>{translate("There is no description available.")}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_htmlContent">
            {parse(transformContentHtml(product.content.htmlContent), parserOptions)}
        </StyledWrapper>
    );
    /* Custom Code */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsDescription),
    definition: {
        displayName: "Description",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        /* Custom Code */
        fieldDefinitions: [
            {
                name: fields.shortDescription,
                displayName: "Short Description",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.anchor,
                displayName: "Anchor",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: x => x.fields.shortDescription,
            },
        ],
        /* Custom Code */
    },
};

export default widgetModule;
